import { useState, useMemo, useEffect } from 'react'
import { Ticket, Building, User } from '../types'
import { loadBuildings, loadExecutors } from '../lib/api'
import { highlightTextMultiple } from '../utils/textHighlight'
import { downloadFile } from '../utils/fileDownload'
import { comparePersonNames } from '../utils/personNameSort'
import { DetailsModal } from './DetailsModal'
import Pagination from './Pagination'
import { useIsMobile } from '../hooks/useMediaQuery'
import { useAlert } from '../contexts/AlertContext'
import './TicketsTable.css'

interface TicketsTableProps {
  tickets: Ticket[]
  onAssignExecutor?: (ticket: Ticket) => void
  onComplete?: (ticketId: string) => void
  onAccelerate?: (ticketId: string) => void
  onDelete?: (ticketId: string) => void
  onAddComment?: (ticketId: string, comment: string) => Promise<void>
  showCompleteButton?: boolean
  showAccelerateButton?: boolean
  showDeleteButton?: boolean
  showAddCommentButton?: boolean
  filterType?: 'requester' | 'executor' | 'admin'  // Тип фильтров в зависимости от роли
  adminType?: 'spravce_it' | 'spravce_budov'  // Тип администратора
}

function TicketsTable({ tickets, onAssignExecutor, onComplete, onAccelerate, onDelete, onAddComment, showCompleteButton, showAccelerateButton, showDeleteButton, showAddCommentButton, filterType = 'requester', adminType }: TicketsTableProps) {
  const { showAlert } = useAlert()
  
  // Состояние фильтров
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [buildingFilter, setBuildingFilter] = useState<string>('all')
  const [executorFilter, setExecutorFilter] = useState<string>('all')
  const [adminFilter, setAdminFilter] = useState<string>('all')
  const [requesterFilter, setRequesterFilter] = useState<string>('all')
  const [searchText, setSearchText] = useState<string>('')
  // Сортировка по дате создания: 'desc' (новые сверху) или 'asc' (старые сверху)
  const [dateSortOrder, setDateSortOrder] = useState<'desc' | 'asc'>('desc')
  
  // Пагинация
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = useIsMobile()
  const itemsPerPage = isMobile ? 20 : 100
  
  // Состояние модального окна
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Состояние для модального окна комментария
  const [commentModalOpen, setCommentModalOpen] = useState(false)
  const [commentTicketId, setCommentTicketId] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)

  // Данные для фильтров
  const [buildings, setBuildings] = useState<Building[]>([])
  const [executors, setExecutors] = useState<User[]>([])
  const [loadingFilters, setLoadingFilters] = useState(false)

  // Получаем уникальных администраторов из заявок
  const administrators = useMemo(() => {
    const adminMap = new Map<string, { name: string, type?: string }>()
    tickets.forEach(ticket => {
      if (ticket.admin_id && ticket.admin_name) {
        if (!adminMap.has(ticket.admin_id)) {
          adminMap.set(ticket.admin_id, {
            name: ticket.admin_name,
            type: ticket.admin_type
          })
        }
      }
    })
    return Array.from(adminMap.entries()).map(([id, data]) => ({ 
      id, 
      name: data.name,
      type: data.type 
    }))
  }, [tickets])

  // Получаем уникальных заявителей (requesters) из заявок
  const requesters = useMemo(() => {
    const requesterMap = new Map<string, { name: string, email?: string }>()
    tickets.forEach(ticket => {
      if (ticket.requester_id && ticket.requester_name) {
        if (!requesterMap.has(ticket.requester_id)) {
          requesterMap.set(ticket.requester_id, {
            name: ticket.requester_name,
            email: ticket.requester_email
          })
        }
      }
    })
    return Array.from(requesterMap.entries()).map(([id, data]) => ({ 
      id, 
      name: data.name,
      email: data.email
    })).sort(comparePersonNames)
  }, [tickets])

  // Загружаем данные для фильтров
  useEffect(() => {
    const loadFilterData = async () => {
      if (filterType === 'executor' || filterType === 'admin') {
        try {
          setLoadingFilters(true)
          const buildingsData = await loadBuildings()
          setBuildings(buildingsData)
        } catch (error) {
          console.error('Chyba při načítání budov:', error)
        } finally {
          setLoadingFilters(false)
        }
      }

      if (filterType === 'admin') {
        try {
          const executorsData = await loadExecutors()
          setExecutors(executorsData)
        } catch (error) {
          console.error('Chyba při načítání řešitelů:', error)
        }
      }
    }

    loadFilterData()
  }, [filterType])

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new':
        return 'Nový'
      case 'in_progress':
        return 'V řešení'
      case 'completed':
        return 'Dokončeno'
      default:
        return status
    }
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'status-new'
      case 'in_progress':
        return 'status-in-progress'
      case 'completed':
        return 'status-completed'
      default:
        return ''
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleRowClick = (ticket: Ticket) => {
    setSelectedTicket(ticket)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedTicket(null)
  }

  // Обработчики для комментариев
  const handleOpenCommentModal = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setCommentTicketId(ticketId)
    setCommentText('')
    setCommentModalOpen(true)
  }

  const handleCloseCommentModal = () => {
    setCommentModalOpen(false)
    setCommentTicketId(null)
    setCommentText('')
  }

  const handleSubmitComment = async () => {
    if (!commentTicketId || !commentText.trim() || !onAddComment) return
    
    try {
      setIsSubmittingComment(true)
      await onAddComment(commentTicketId, commentText.trim())
      handleCloseCommentModal()
      showAlert('Komentář byl úspěšně přidán', 'success')
    } catch (error: any) {
      console.error('Chyba při přidávání komentáře:', error)
      showAlert('Chyba při přidávání komentáře', 'error')
    } finally {
      setIsSubmittingComment(false)
    }
  }

  // Фильтрация и сортировка
  const filteredAndSortedTickets = useMemo(() => {
    let filtered = [...tickets]

    // Поиск по словам - для всех ролей
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim()
      filtered = filtered.filter(t => {
        const description = (t.description || '').toLowerCase()
        const requesterName = (t.requester_name || '').toLowerCase()
        const buildingName = (t.building_name || '').toLowerCase()
        const executorName = (t.executor_name || '').toLowerCase()
        const phone = (t.phone || '').toLowerCase()
        const status = getStatusLabel(t.status).toLowerCase()
        const requesterEmail = (t.requester_email || '').toLowerCase()
        
        return description.includes(searchLower) ||
               requesterName.includes(searchLower) ||
               buildingName.includes(searchLower) ||
               executorName.includes(searchLower) ||
               phone.includes(searchLower) ||
               status.includes(searchLower) ||
               requesterEmail.includes(searchLower)
      })
    }

    // Фильтр по статусу (для всех ролей кроме správce_budov)
    if (adminType !== 'spravce_budov' && statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter)
    }

    // Фильтр по зданию (для executor и admin)
    if ((filterType === 'executor' || filterType === 'admin') && buildingFilter !== 'all') {
      filtered = filtered.filter(t => t.building_id === buildingFilter)
    }

    // Фильтр по исполнителю (только для admin)
    if (filterType === 'admin' && executorFilter !== 'all') {
      filtered = filtered.filter((t) => t.executor_id === executorFilter)
    }

    // Фильтр по администратору (только для admin)
    if (filterType === 'admin' && adminFilter !== 'all') {
      filtered = filtered.filter(t => t.admin_id === adminFilter)
    }

    // Фильтр по заявителю (только для admin)
    if (filterType === 'admin' && requesterFilter !== 'all') {
      filtered = filtered.filter(t => t.requester_id === requesterFilter)
    }

    // Сортировка по дате создания (с возможностью переключения)
    filtered.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime()
      const bTime = new Date(b.created_at).getTime()
      if (dateSortOrder === 'desc') {
        return bTime - aTime // DESC - новые сверху
      } else {
        return aTime - bTime // ASC - старые сверху
      }
    })

    return filtered
  }, [tickets, statusFilter, buildingFilter, executorFilter, adminFilter, requesterFilter, filterType, adminType, searchText, dateSortOrder])

  // Пагинация отсортированных данных
  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredAndSortedTickets.slice(startIndex, endIndex)
  }, [filteredAndSortedTickets, currentPage, itemsPerPage])

  const totalPages = Math.ceil(filteredAndSortedTickets.length / itemsPerPage)

  // Сброс страницы при изменении фильтров или сортировки
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, buildingFilter, executorFilter, adminFilter, requesterFilter, searchText, dateSortOrder])

  // Сброс фильтров
  const resetFilters = () => {
    setStatusFilter('all')
    setBuildingFilter('all')
    setExecutorFilter('all')
    setAdminFilter('all')
    setRequesterFilter('all')
    setSearchText('')
    setDateSortOrder('desc') // Сбрасываем сортировку на новые сверху
    setCurrentPage(1)
  }

  if (tickets.length === 0) {
    return (
      <div className="tickets-table-empty">
        <p>Žádné žádosti k zobrazení</p>
      </div>
    )
  }

  return (
    <div className="tickets-table-container">
      <div className="table-filters">
        <div className="filter-row">
          {/* Поиск по словам - для всех ролей */}
          <div className="filter-group">
            <label>Hledat:</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Hledat v žádostech..."
              className="filter-select"
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            {searchText && (
              <button
                onClick={() => setSearchText('')}
                style={{
                  marginLeft: '0.5rem',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Фильтр по статусу - только если не správce_budov */}
          {adminType !== 'spravce_budov' && (
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Všechny</option>
                <option value="new">Nový</option>
                <option value="in_progress">V řešení</option>
                <option value="completed">Dokončeno</option>
              </select>
            </div>
          )}

          {/* Фильтр по зданию - для executor и admin */}
          {(filterType === 'executor' || filterType === 'admin') && (
            <div className="filter-group">
              <label>Budova:</label>
              <select 
                value={buildingFilter} 
                onChange={(e) => setBuildingFilter(e.target.value)}
                className="filter-select"
                disabled={loadingFilters}
              >
                <option value="all">Všechny</option>
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>{building.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Фильтр по исполнителю - только для admin */}
          {filterType === 'admin' && (
            <div className="filter-group">
              <label>Řešitel:</label>
              <select 
                value={executorFilter} 
                onChange={(e) => setExecutorFilter(e.target.value)}
                className="filter-select"
                disabled={loadingFilters}
              >
                <option value="all">Všichni</option>
                {executors.map(executor => (
                  <option key={executor.id} value={executor.id}>{executor.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Фильтр по администратору - только для admin */}
          {filterType === 'admin' && (
            <div className="filter-group">
              <label>Administrátor:</label>
              <select 
                value={adminFilter} 
                onChange={(e) => setAdminFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Všichni</option>
                {administrators.map(admin => (
                  <option key={admin.id} value={admin.id}>
                    {admin.name} {admin.type === 'spravce_it' ? '(IT)' : admin.type === 'spravce_budov' ? '(Budovy)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Фильтр по заявителю (от кого пришла заявка) - только для admin */}
          {filterType === 'admin' && (
            <div className="filter-group">
              <label>Žadatel:</label>
              <select 
                value={requesterFilter} 
                onChange={(e) => setRequesterFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Všichni</option>
                {requesters.map(requester => (
                  <option key={requester.id} value={requester.id}>
                    {requester.name} {requester.email ? `(${requester.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button 
            onClick={resetFilters}
            className="reset-filters-button"
            title="Resetovat filtry"
          >
            🔄 Reset
          </button>
        </div>
        <div className="filter-info">
          Zobrazeno: {filteredAndSortedTickets.length} z {tickets.length} žádostí
        </div>
      </div>

      <table className="tickets-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Budova</th>
            <th>Žadatel</th>
            <th>Telefon</th>
            <th>Řešitel</th>
            <th>Popis</th>
            <th>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setDateSortOrder(dateSortOrder === 'desc' ? 'asc' : 'desc') }}>
                Vytvořeno
                <span style={{ fontSize: '0.8rem', userSelect: 'none' }}>
                  {dateSortOrder === 'desc' ? '↓' : '↑'}
                </span>
              </div>
            </th>
            <th>Přiřazeno</th>
            <th>Dokončeno</th>
            <th>Akce</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTickets.length === 0 ? (
            <tr>
              <td colSpan={11} className="no-results">
                Žádné žádosti neodpovídají zadaným filtrům
              </td>
            </tr>
          ) : (
            paginatedTickets.map(ticket => (
            <tr 
              key={ticket.id}
              className={`clickable-row ${ticket.admin_type === 'spravce_budov' ? 'ticket-building-admin-row' : ''}`}
              style={ticket.admin_type === 'spravce_budov' ? { backgroundColor: '#fff9e6' } : {}}
              onClick={() => handleRowClick(ticket)}
            >
              <td className="ticket-id-cell">#{ticket.id}</td>
              <td>
                <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                  {highlightTextMultiple(getStatusLabel(ticket.status), searchText)}
                </span>
              </td>
              <td>{highlightTextMultiple(ticket.building_name || 'Neznámá', searchText)}</td>
              <td>
                {highlightTextMultiple(ticket.requester_name || 'Neznámý', searchText)}
                {ticket.requester_email && (
                  <div>
                    <a href={`mailto:${ticket.requester_email}`} className="email-link">
                      {highlightTextMultiple(ticket.requester_email, searchText)}
                    </a>
                  </div>
                )}
              </td>
              <td>
                {ticket.phone ? (
                  <a href={`tel:${ticket.phone}`} className="phone-link">
                    {highlightTextMultiple(ticket.phone, searchText)}
                  </a>
                ) : (
                  '-'
                )}
              </td>
              <td>{highlightTextMultiple(ticket.executor_name || '-', searchText)}</td>
              <td className="description-cell" title={ticket.description}>
                {highlightTextMultiple(
                  ticket.description.length > 50 
                    ? `${ticket.description.substring(0, 50)}...` 
                    : ticket.description,
                  searchText
                )}
              </td>
              <td>{formatDate(ticket.created_at)}</td>
              <td>
                {ticket.assigned_at ? formatDate(ticket.assigned_at) : '-'}
              </td>
              <td>
                {ticket.completed_at ? formatDate(ticket.completed_at) : '-'}
              </td>
              <td>
                <div className="table-actions">
                  {/* 1. Кнопка комментария (для новых заявок) */}
                  {showAddCommentButton && onAddComment && (ticket.status === 'new' || ticket.status === 'in_progress') && (
                    <button
                      className="comment-button-small"
                      onClick={(e) => handleOpenCommentModal(ticket.id, e)}
                      title="Přidat komentář"
                    >
                      💬
                    </button>
                  )}
                  
                  {/* 2. Кнопка назначения исполнителя */}
                  {(ticket.status === 'new' || ticket.status === 'in_progress') && onAssignExecutor && (
                    <button
                      className={ticket.status === 'new' ? 'reassign-button-small' : 'assign-button-small'}
                      onClick={(e) => {
                        e.stopPropagation()
                        onAssignExecutor(ticket)
                      }}
                      title={ticket.status === 'new' ? "Přiřadit řešitele" : "Změnit řešitele"}
                    >
                      {ticket.status === 'new' ? '👤 Přiřadit' : '🔄 Změnit'}
                    </button>
                  )}
                  
                  {/* 3. Кнопка завершения */}
                  {showCompleteButton && onComplete && ticket.status === 'in_progress' && (
                    <button
                      className="complete-button-small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onComplete(ticket.id)
                      }}
                      title="Označit jako hotovo"
                    >
                      ✅ Hotovo
                    </button>
                  )}
                  
                  {/* 4. Кнопка ускорения */}
                  {showAccelerateButton && onAccelerate && ticket.status !== 'completed' && (
                    <button
                      className="accelerate-button-small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onAccelerate(ticket.id)
                      }}
                      title="Požádat o urychlení"
                    >
                      ⚡ Urychlit
                    </button>
                  )}
                  
                  {/* 5. Ссылка на файл */}
                  {ticket.file_name && ticket.file_url && (
                    <a
                      href="#"
                      onClick={async (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        try {
                          await downloadFile(ticket.file_url || ticket.file_name || '', ticket.id, undefined, false)
                        } catch (error: any) {
                          showAlert('Chyba při otevírání souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                        }
                      }}
                      className="file-link-small"
                      title={`Otevřít přílohu: ${ticket.file_name}`}
                    >
                      📎
                    </a>
                  )}
                  
                  {/* 6. Кнопка удаления */}
                  {showDeleteButton && onDelete && (
                    <button
                      className="delete-button-small"
                      onClick={(e) => {
                        e.stopPropagation()
                        if (confirm(`Opravdu chcete smazat žádost #${ticket.id}?`)) {
                          onDelete(ticket.id)
                        }
                      }}
                      title="Smazat žádost"
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        marginLeft: '0.25rem'
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </td>
            </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredAndSortedTickets.length}
        />
      )}

      {/* Модальное окно с деталями */}
      {selectedTicket && (
        <DetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`Žádost #${selectedTicket.id}`}
          actions={
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {(selectedTicket.status === 'new' || selectedTicket.status === 'in_progress') && onAssignExecutor && (
                <button
                  className={selectedTicket.status === 'new' ? 'reassign-button-small' : 'assign-button-small'}
                  onClick={() => {
                    onAssignExecutor(selectedTicket)
                    handleCloseModal()
                  }}
                >
                  {selectedTicket.status === 'new' ? 'Přiřadit řešitele' : 'Změnit řešitele'}
                </button>
              )}
              {showCompleteButton && onComplete && selectedTicket.status === 'in_progress' && (
                <button
                  className="complete-button-small"
                  onClick={() => {
                    onComplete(selectedTicket.id)
                    handleCloseModal()
                  }}
                >
                  HOTOVO
                </button>
              )}
              {showAccelerateButton && onAccelerate && selectedTicket.status !== 'completed' && (
                <button
                  className="accelerate-button-small"
                  onClick={() => {
                    onAccelerate(selectedTicket.id)
                    handleCloseModal()
                  }}
                >
                  ⚡ Urychlit
                </button>
              )}
              {showDeleteButton && onDelete && (
                <button
                  className="delete-button-small"
                  onClick={() => {
                    if (confirm(`Opravdu chcete smazat žádost #${selectedTicket.id}?`)) {
                      onDelete(selectedTicket.id)
                      handleCloseModal()
                    }
                  }}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🗑️ Smazat
                </button>
              )}
            </div>
          }
        >
          <div className="details-section">
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Status</div>
                <div className="details-value">
                  <span className={`status-badge ${getStatusClass(selectedTicket.status)}`}>
                    {getStatusLabel(selectedTicket.status)}
                  </span>
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Budova</div>
                <div className="details-value">{selectedTicket.building_name || 'Neznámá'}</div>
              </div>
              <div className="details-item">
                <div className="details-label">Žadatel</div>
                <div className="details-value">
                  <strong>{selectedTicket.requester_name || 'Neznámý'}</strong>
                  {selectedTicket.requester_email && (
                    <div>
                      <a href={`mailto:${selectedTicket.requester_email}`} className="email-link">
                        {selectedTicket.requester_email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Telefon</div>
                <div className="details-value">
                  {selectedTicket.phone ? (
                    <a href={`tel:${selectedTicket.phone}`} className="phone-link">
                      {selectedTicket.phone}
                    </a>
                  ) : '-'}
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Řešitel</div>
                <div className="details-value">{selectedTicket.executor_name || '-'}</div>
              </div>
              <div className="details-item">
                <div className="details-label">Vytvořeno</div>
                <div className="details-value">{formatDate(selectedTicket.created_at)}</div>
              </div>
              <div className="details-item">
                <div className="details-label">Přiřazeno</div>
                <div className="details-value">
                  {selectedTicket.assigned_at ? formatDate(selectedTicket.assigned_at) : '-'}
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Dokončeno</div>
                <div className="details-value">
                  {selectedTicket.completed_at ? formatDate(selectedTicket.completed_at) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className="details-section">
            <div className="details-section-title">Popis</div>
            <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
              {selectedTicket.description}
            </div>
          </div>
          {selectedTicket.completion_comment ? (
            <div className="details-section">
              <div className="details-section-title">Komentář k dokončení</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedTicket.completion_comment}
              </div>
            </div>
          ) : null}
          {selectedTicket.file_name && selectedTicket.file_url && (
            <div className="details-section">
              <div className="details-section-title">Příloha</div>
              <div className="details-value">
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    try {
                      await downloadFile(selectedTicket.file_url || selectedTicket.file_name || '', selectedTicket.id, undefined, false)
                    } catch (error: any) {
                      showAlert('Chyba při otevírání souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                    }
                  }}
                  className="file-link"
                  title={`Otevřít přílohu: ${selectedTicket.file_name}`}
                >
                  📎 {selectedTicket.file_name}
                </a>
                <span style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      try {
                        await downloadFile(selectedTicket.file_url || selectedTicket.file_name || '', selectedTicket.id, undefined, true)
                      } catch (error: any) {
                        showAlert('Chyba při stahování souboru: ' + (error.message || 'Neznámá chyba'), 'error')
                      }
                    }}
                    className="file-link"
                    title="Stáhnout soubor"
                  >
                    ⬇️ Stáhnout
                  </a>
                </span>
              </div>
            </div>
          )}
        </DetailsModal>
      )}

      {/* Модальное окно для добавления комментария */}
      {commentModalOpen && (
        <div
          className="modal-overlay"
          onClick={handleCloseCommentModal}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="modal-header">
              <h3>💬 Přidat komentář k žádosti #{commentTicketId}</h3>
              <button
                className="modal-close"
                onClick={handleCloseCommentModal}
                disabled={isSubmittingComment}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Napište váš komentář..."
                rows={5}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                disabled={isSubmittingComment}
                autoFocus
              />
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                Komentář bude přidán k popisu žádosti s vaším jménem a časem.
              </p>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={handleCloseCommentModal}
                disabled={isSubmittingComment}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmittingComment ? 'not-allowed' : 'pointer',
                  opacity: isSubmittingComment ? 0.6 : 1
                }}
              >
                Zrušit
              </button>
              <button
                onClick={handleSubmitComment}
                disabled={!commentText.trim() || isSubmittingComment}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#14b8a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!commentText.trim() || isSubmittingComment) ? 'not-allowed' : 'pointer',
                  opacity: (!commentText.trim() || isSubmittingComment) ? 0.6 : 1
                }}
              >
                {isSubmittingComment ? 'Přidávání...' : 'Přidat komentář'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TicketsTable
